import { fetchWithAuth, getApiBaseUrl } from './api';
import { okJson } from './httpError';
import { normalizePhotoForUpload } from '../utils/uploadPhoto';

const base = () => getApiBaseUrl();

export type EncomendaStatus = 'received' | 'notified' | 'picked_up' | 'returned';

export interface EncomendaEvent {
  id: string;
  encomenda_id: string;
  event_type: string;
  notes?: string;
  signature_data?: string;
  created_at: string;
  created_by?: string;
  created_by_name?: string;
}

export interface Encomenda {
  id: string;
  customer_id: string;
  unit_id: string;
  person_id?: string;
  status: EncomendaStatus;
  transportadora?: string;
  tracking_code?: string;
  description?: string;
  photo_url?: string;
  photo_url_2?: string;
  photo_url_3?: string;
  photo_url_4?: string;
  photo_url_5?: string;
  notified_at?: string;
  picked_up_at?: string;
  returned_at?: string;
  created_at: string;
  updated_at: string;
  created_by_name?: string;
  events?: EncomendaEvent[];
  person?: {
    id: string;
    first_name: string;
    last_name: string;
    cpf?: string;
    phone?: string;
  };
  department_id?: string;
  unit?: {
    id: string;
    number: string;
    block?: { id: string; name: string };
    street?: { id: string; name: string };
    street_number?: string;
  };
}

export interface CreateEncomendaRequest {
  unit_id?: string;        // required for condominio
  department_id?: string;  // required for empresa (used instead of unit_id)
  person_id?: string;
  transportadora?: string;
  tracking_code?: string;
  description?: string;
  photo_url?: string;
  photo_url_2?: string;
  photo_url_3?: string;
  photo_url_4?: string;
  photo_url_5?: string;
  notes?: string;
}

// Collects the up-to-5 photo URLs on an encomenda into a single array (skips empty slots).
export function encomendaPhotoUrls(enc: Pick<Encomenda, 'photo_url' | 'photo_url_2' | 'photo_url_3' | 'photo_url_4' | 'photo_url_5'>): string[] {
  return [enc.photo_url, enc.photo_url_2, enc.photo_url_3, enc.photo_url_4, enc.photo_url_5].filter((u): u is string => !!u);
}

export interface ListEncomendaParams {
  // Backend accepts a comma-separated status list (encomenda_handler.go ListEncomendas) —
  // pass an array to fetch multiple statuses in one request instead of one call per status.
  status?: EncomendaStatus | EncomendaStatus[] | '';
  unit_id?: string;
  person_id?: string;
}

export async function listEncomendas(
  customerId: string,
  params?: ListEncomendaParams
): Promise<{ success: boolean; data: Encomenda[] }> {
  const qs = new URLSearchParams();
  if (params?.status) {
    qs.set('status', Array.isArray(params.status) ? params.status.join(',') : params.status);
  }
  if (params?.unit_id) qs.set('unit_id', params.unit_id);
  if (params?.person_id) qs.set('person_id', params.person_id);
  const query = qs.toString() ? `?${qs.toString()}` : '';
  const r = await fetchWithAuth(`${base()}/api/v1/customers/${customerId}/encomendas${query}`);
  if (!r.ok) throw new Error('Failed to fetch encomendas');
  return r.json();
}

export async function getEncomenda(
  customerId: string,
  encomendaId: string
): Promise<{ success: boolean; data: Encomenda }> {
  const r = await fetchWithAuth(`${base()}/api/v1/customers/${customerId}/encomendas/${encomendaId}`);
  if (!r.ok) throw new Error('Failed to fetch encomenda');
  return r.json();
}

export async function createEncomenda(
  customerId: string,
  data: CreateEncomendaRequest
): Promise<{ success: boolean; data: Encomenda; message?: string }> {
  const r = await fetchWithAuth(`${base()}/api/v1/customers/${customerId}/encomendas`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  const json = await okJson<any>(r, 'Erro ao criar a encomenda');
  return json;
}

export async function updateEncomendaStatus(
  customerId: string,
  encomendaId: string,
  status: EncomendaStatus,
  opts?: { notes?: string; signature_data?: string }
): Promise<{ success: boolean; data: Encomenda; message?: string }> {
  const r = await fetchWithAuth(
    `${base()}/api/v1/customers/${customerId}/encomendas/${encomendaId}/status`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status, notes: opts?.notes, signature_data: opts?.signature_data }),
    }
  );
  const json = await okJson<any>(r, 'Erro ao atualizar o status da encomenda');
  return json;
}

export async function uploadEncomendaPhoto(
  customerId: string,
  file: File
): Promise<{ success: boolean; photo_url: string }> {
  const formData = new FormData();
  formData.append('photo', await normalizePhotoForUpload(file));
  const r = await fetchWithAuth(`${base()}/api/v1/customers/${customerId}/encomendas/photo`, {
    method: 'POST',
    body: formData,
  });
  const json = await okJson<any>(r, 'Erro ao enviar a foto');
  return json;
}

export async function notifyEncomenda(
  customerId: string,
  encomendaId: string
): Promise<{ success: boolean; message?: string; sent?: boolean }> {
  const r = await fetchWithAuth(
    `${base()}/api/v1/customers/${customerId}/encomendas/${encomendaId}/notify`,
    { method: 'POST' }
  );
  const json = await okJson<any>(r, 'Erro ao enviar a notificação');
  return json;
}

export async function deleteEncomenda(
  customerId: string,
  encomendaId: string
): Promise<{ success: boolean; message?: string }> {
  const r = await fetchWithAuth(
    `${base()}/api/v1/customers/${customerId}/encomendas/${encomendaId}`,
    { method: 'DELETE' }
  );
  const json = await okJson<any>(r, 'Erro ao excluir a encomenda');
  return json;
}

// Status helpers
export const ENCOMENDA_STATUS_LABELS: Record<EncomendaStatus, string> = {
  received: 'Aguardando retirada',
  notified: 'Morador notificado',
  picked_up: 'Retirada',
  returned: 'Devolvida',
};

/**
 * DARK-theme badges: 400-weight text on a 10% wash of itself only works against a dark surface.
 * On white the same values are roughly 2:1 contrast — a pale smear. Use ENCOMENDA_STATUS_COLORS_LIGHT
 * on light surfaces.
 */
export const ENCOMENDA_STATUS_COLORS: Record<EncomendaStatus, string> = {
  received: 'text-yellow-400 bg-yellow-400/10 border-yellow-400/30',
  notified: 'text-blue-400 bg-blue-400/10 border-blue-400/30',
  picked_up: 'text-emerald-400 bg-emerald-400/10 border-emerald-400/30',
  returned: 'text-gray-400 bg-gray-400/10 border-gray-400/30',
};

/** Light-theme counterpart — 700-weight text on a 50-weight tint, which reads on white. */
export const ENCOMENDA_STATUS_COLORS_LIGHT: Record<EncomendaStatus, string> = {
  received: 'bg-amber-50 text-amber-700 border-amber-200',
  notified: 'bg-blue-50 text-blue-700 border-blue-200',
  picked_up: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  returned: 'bg-gray-100 text-gray-600 border-gray-200',
};
